import { type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface Column<T> {
  key: string;
  header: string;
  /** Cell renderer — return any ReactNode. Defaults to a stringified field lookup. */
  render?: (row: T) => ReactNode;
  className?: string;
  /** If true, take up extra horizontal space. */
  grow?: boolean;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  loading?: boolean;
  /** Empty state message. */
  emptyMessage?: string;
  /** Stable row id extractor. */
  rowKey: (row: T) => string;
  /** Optional action column rendered on the far right. */
  actions?: (row: T) => ReactNode;
  /** Total rows for footer; only shown if provided. */
  total?: number;
}

/**
 * Lean table component. Not virtualized — fine for ≤500 rows.
 * For larger lists, paginate server-side (the API already supports page+pageSize).
 */
export function DataTable<T>({
  columns,
  rows,
  loading,
  emptyMessage = 'No records yet.',
  rowKey,
  actions,
  total,
}: DataTableProps<T>) {
  return (
    <div className="overflow-hidden rounded-lg border border-brand-gray-light bg-white shadow-card">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-brand-off-white text-xs font-semibold uppercase tracking-wider text-brand-gray-mid">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className={cn('px-4 py-3', col.grow && 'w-full', col.className)}
                >
                  {col.header}
                </th>
              ))}
              {actions && <th scope="col" className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-brand-gray-light">
            {loading && (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="px-4 py-10 text-center text-brand-gray-mid">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="Loading" />
                </td>
              </tr>
            )}
            {!loading && rows && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="px-4 py-10 text-center text-brand-gray-mid">
                  {emptyMessage}
                </td>
              </tr>
            )}
            {!loading && rows?.map((row) => (
              <tr key={rowKey(row)} className="hover:bg-brand-off-white">
                {columns.map((col) => (
                  <td key={col.key} className={cn('px-4 py-3 text-brand-charcoal', col.className)}>
                    {col.render
                      ? col.render(row)
                      : String((row as Record<string, unknown>)[col.key] ?? '')}
                  </td>
                ))}
                {actions && (
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    {actions(row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {typeof total === 'number' && (
        <div className="border-t border-brand-gray-light bg-brand-off-white px-4 py-2 text-right text-xs text-brand-gray-mid">
          {total} record{total === 1 ? '' : 's'}
        </div>
      )}
    </div>
  );
}
